import {
  applyMove,
  chooseMove,
  createGame,
  Coord,
  Difficulty,
  Disc,
  GameState,
  opponent,
} from '../engine';
import { GameVM, GameView, Outcome } from './gameView';
import { DiscColor, HomeConfig, HomeView } from './homeView';
import { Sound } from './sound';

const AI_THINK_MS = 420; // small pause so the bot's move feels deliberate
const RESULT_DELAY_MS = 760; // let the winning line register before the modal

type Screen = 'home' | 'game';

/**
 * Top-level controller. Owns the screens and all session state the engine
 * doesn't: which colour the human plays, running scores, the undo stack, and
 * AI scheduling. The engine itself stays pure and untouched.
 */
export class ConnectFourApp {
  private screen: Screen = 'home';
  private difficulty: Difficulty = 'medium';
  private humanDisc: Disc = 1; // red
  private aiDisc: Disc = 2; // yellow

  private state: GameState = createGame(1);
  private lastMove: Coord | null = null;
  private locked = false; // true while the AI "thinks" or the game is over
  private ghostCol: number | null = null;
  private scores = { you: 0, bot: 0 };
  private readonly undoStack: GameState[] = []; // states captured before each human move
  private aiTimer: number | null = null;

  private soundOn = true;
  private readonly sound = new Sound();

  private readonly home: HomeView;
  private readonly game: GameView;

  constructor(root: HTMLElement) {
    this.home = new HomeView(root, { onStart: (cfg) => this.startMatch(cfg) });
    this.game = new GameView(root, {
      onColumn: (col) => this.handleColumn(col),
      onHover: (col) => this.handleHover(col),
      onUndo: () => this.handleUndo(),
      onRestart: () => this.newGame(),
      onToggleSound: () => this.toggleSound(),
      onRematch: () => this.newGame(),
      onMenu: () => this.gotoHome(),
    });

    // Unlock audio on the first user gesture (browser autoplay policy), so the
    // context is already "running" before any sound is scheduled — including the
    // AI's first move, which fires from a timer rather than a click.
    const unlock = () => {
      this.sound.unlock();
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);

    this.gotoHome();
  }

  // --- screen transitions -------------------------------------------------

  private gotoHome(): void {
    this.cancelAi();
    this.screen = 'home';
    this.game.hide();
    this.home.show();
  }

  private startMatch(cfg: HomeConfig): void {
    this.difficulty = cfg.difficulty;
    this.setColor(cfg.color);
    this.scores = { you: 0, bot: 0 };
    this.screen = 'game';
    this.home.hide();
    this.game.show();
    this.newGame();
  }

  /** Red always moves first; choosing yellow means the bot opens. */
  private setColor(color: DiscColor): void {
    this.humanDisc = color === 'red' ? 1 : 2;
    this.aiDisc = opponent(this.humanDisc);
  }

  /** Fresh board, keeping the chosen colour, difficulty, and running scores. */
  private newGame(): void {
    this.cancelAi();
    this.state = createGame(1); // disc 1 (red) starts
    this.lastMove = null;
    this.locked = false;
    this.ghostCol = null;
    this.undoStack.length = 0;
    this.game.hideResult();
    this.refresh();

    if (this.state.currentPlayer === this.aiDisc) this.scheduleAi();
  }

  // --- gameplay -----------------------------------------------------------

  private handleColumn(col: number): void {
    if (this.locked || this.state.status !== 'playing') return;
    if (this.state.currentPlayer !== this.humanDisc) return;

    const snapshot = this.state;
    const next = applyMove(this.state, col);
    if (next === this.state) return; // illegal / full column — no-op

    this.undoStack.push(snapshot);
    this.commitMove(next, col);

    if (this.state.status === 'playing') this.scheduleAi();
    else this.endGame();
  }

  private scheduleAi(): void {
    this.locked = true;
    this.ghostCol = null;
    this.refresh();

    this.aiTimer = window.setTimeout(() => {
      this.aiTimer = null;
      const col = chooseMove(this.state.board, this.aiDisc, this.difficulty);
      this.commitMove(applyMove(this.state, col), col);
      this.locked = false;

      if (this.state.status === 'playing') this.refresh();
      else this.endGame();
    }, AI_THINK_MS);
  }

  private commitMove(next: GameState, col: number): void {
    this.state = next;
    this.lastMove = topDiscCoord(next, col);
    this.sound.drop();
    this.refresh();
  }

  private endGame(): void {
    this.locked = true; // freeze the board; the modal takes over
    const outcome = this.outcome();
    if (outcome === 'win') this.scores.you += 1;
    else if (outcome === 'lose') this.scores.bot += 1;
    this.refresh();

    window.setTimeout(() => {
      this.playResultSound(outcome);
      this.game.showResult(outcome, this.scores);
    }, RESULT_DELAY_MS);
  }

  private handleUndo(): void {
    if (!this.canUndo()) return;
    const restored = this.undoStack.pop();
    if (!restored) return;
    this.state = restored;
    this.lastMove = null;
    this.locked = false;
    this.refresh();
  }

  private handleHover(col: number | null): void {
    this.ghostCol = col;
    if (this.screen === 'game') this.refresh();
  }

  private toggleSound(): void {
    this.soundOn = !this.soundOn;
    this.sound.setEnabled(this.soundOn);
    this.refresh();
  }

  // --- helpers ------------------------------------------------------------

  private canUndo(): boolean {
    return (
      this.screen === 'game' &&
      this.state.status === 'playing' &&
      this.state.currentPlayer === this.humanDisc &&
      !this.locked &&
      this.undoStack.length > 0
    );
  }

  private outcome(): Outcome {
    if (this.state.status === 'draw') return 'draw';
    return this.state.winner === this.humanDisc ? 'win' : 'lose';
  }

  private playResultSound(outcome: Outcome): void {
    if (outcome === 'win') this.sound.win();
    else if (outcome === 'lose') this.sound.lose();
    else this.sound.draw();
  }

  private cancelAi(): void {
    if (this.aiTimer !== null) {
      window.clearTimeout(this.aiTimer);
      this.aiTimer = null;
    }
  }

  private refresh(): void {
    const vm: GameVM = {
      board: this.state.board,
      winningLine: this.state.winningLine,
      status: this.state.status,
      current: this.state.currentPlayer,
      humanDisc: this.humanDisc,
      aiDisc: this.aiDisc,
      locked: this.locked,
      ghostCol: this.ghostCol,
      lastMove: this.lastMove,
      scores: this.scores,
      difficulty: this.difficulty,
      soundOn: this.soundOn,
      canUndo: this.canUndo(),
    };
    this.game.update(vm);
  }
}

/** The most-recently dropped disc in a column is its topmost filled cell. */
function topDiscCoord(state: GameState, col: number): Coord | null {
  for (let row = 0; row < state.board.length; row++) {
    if (state.board[row][col] !== 0) return { row, col };
  }
  return null;
}
